import { useEffect, useState, useCallback } from 'react';
import { SystemNotificationEvent, subscribeToSystemNotifications } from '@/api/notifications.api';

/**
 * @file useSystemNotifications.ts
 * @description SysNot-1: per-tab subscription to the system notifications
 * SSE stream. Returns a small ring-buffer of the most recent N events
 * plus a `dismiss(idx)` callback to remove a specific entry from the UI
 * without affecting the underlying stream.
 *
 * @why_ring_buffer
 * The Layout-level toast stack is short-lived. We only keep the latest
 * MAX_BUFFERED in state because the channel is push-only and history
 * reconstruction is not the goal of this surface. For history the user
 * has the dedicated "Reminders" / "Memory" pages, fed by REST endpoints.
 */
const MAX_BUFFERED = 5;

interface BufferedNotification extends SystemNotificationEvent {
  /** Stable key for React lists — `${timestamp}-${idx}` collisions avoided. */
  uid: string;
}

export function useSystemNotifications(): {
  events: BufferedNotification[];
  dismiss: (uid: string) => void;
} {
  const [events, setEvents] = useState<BufferedNotification[]>([]);

  useEffect(() => {
    // uid через crypto.randomUUID — гарантированно уникален между ремаунтами
    // (React StrictMode в dev монтирует дважды — counter-based uid давал
    // дубликаты при совпадении timestamp в одну миллисекунду).
    const unsubscribe = subscribeToSystemNotifications((event) => {
      const uid = crypto.randomUUID();
      setEvents((prev) => {
        const next = [{ ...event, uid }, ...prev];
        return next.slice(0, MAX_BUFFERED);
      });
    });
    return unsubscribe;
  }, []);

  const dismiss = useCallback((uid: string) => {
    setEvents((prev) => prev.filter((e) => e.uid !== uid));
  }, []);

  return { events, dismiss };
}
